import {
	Certificate,
	lookupResultToBuffer,
	type HttpAgent,
} from "@icp-sdk/core/agent";
import { Principal } from "@icp-sdk/core/principal";
import { sha256 } from "@noble/hashes/sha2";

export type CertifiedAnalyticsSummaryE8 = {
	tvlE8: bigint;
	totalDebtE8: bigint;
	activeVaults: bigint;
	liquidatableCount: bigint;
	atRiskCount: bigint;
	savingsDepositedE8: bigint;
	totalRepaidE8: bigint;
};

export type CertVerifyFailure =
	| "empty_certificate"
	| "no_root_key"
	| "lookup_miss"
	| "hash_mismatch"
	| "exception";

export type CertVerifyResult =
	| { ok: true }
	| { ok: false; reason: CertVerifyFailure };

function summaryPayload(summary: CertifiedAnalyticsSummaryE8): string {
	return [
		summary.tvlE8,
		summary.totalDebtE8,
		summary.activeVaults,
		summary.liquidatableCount,
		summary.atRiskCount,
		summary.savingsDepositedE8,
		summary.totalRepaidE8,
	].join(":");
}

/** Ensure agent has a root key (mainnet IC_ROOT_KEY or fetched for local). */
export async function ensureAgentRootKey(agent: HttpAgent): Promise<boolean> {
	if (agent.rootKey && agent.rootKey.length > 0) return true;
	try {
		await agent.fetchRootKey();
		return Boolean(agent.rootKey && agent.rootKey.length > 0);
	} catch {
		return false;
	}
}

/**
 * Fail-closed verification of protocol getCertifiedAnalyticsSummary.
 */
export async function verifyCertifiedAnalyticsSummary(
	agent: HttpAgent,
	canisterId: string,
	certificateBytes: Uint8Array,
	summary: CertifiedAnalyticsSummaryE8,
): Promise<boolean> {
	const r = await verifyCertifiedAnalyticsSummaryDetailed(
		agent,
		canisterId,
		certificateBytes,
		summary,
	);
	return r.ok;
}

export async function verifyCertifiedAnalyticsSummaryDetailed(
	agent: HttpAgent,
	canisterId: string,
	certificateBytes: Uint8Array,
	summary: CertifiedAnalyticsSummaryE8,
): Promise<CertVerifyResult> {
	if (certificateBytes.length === 0) {
		return { ok: false, reason: "empty_certificate" };
	}
	try {
		const hasKey = await ensureAgentRootKey(agent);
		if (!hasKey || !agent.rootKey) {
			return { ok: false, reason: "no_root_key" };
		}
		const cert = await Certificate.create({
			certificate: certificateBytes,
			rootKey: agent.rootKey,
			principal: { canisterId: Principal.fromText(canisterId) },
			agent,
		});
		const certified = lookupResultToBuffer(
			cert.lookup_path(["certified_data"]),
		);
		if (!certified) return { ok: false, reason: "lookup_miss" };
		const expected = sha256(new TextEncoder().encode(summaryPayload(summary)));
		if (certified.length !== expected.length) {
			return { ok: false, reason: "hash_mismatch" };
		}
		const match = certified.every((byte, i) => byte === expected[i]);
		return match ? { ok: true } : { ok: false, reason: "hash_mismatch" };
	} catch {
		return { ok: false, reason: "exception" };
	}
}

export function e8ToUsd(e8: bigint): number {
	return Number(e8) / 1e8;
}

/** Last cert-verify failure for env diagnostics (session). */
let lastCertFail: CertVerifyFailure | null = null;

export function getLastCertVerifyFailure(): CertVerifyFailure | null {
	return lastCertFail;
}

export function setLastCertVerifyFailure(reason: CertVerifyFailure | null): void {
	lastCertFail = reason;
}
